import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';
import { useAnalyticsStatsContextOrDefault } from '~/views/Dashboard/AnalyticsStats/AnalyticsProvider';
import {
  analyticsAuthoringQueryKeyPart,
  analyticsQueryParams,
  statMetadataModeFromAnalyticsMode,
  type AnalyticsMode,
} from '~/views/Dashboard/AnalyticsStats/analyticsMode';

import { analyticsStatCategorySchema } from '../analyticsStatCategory';

const statValueTypeSchema = z.union([
  z.literal('number'),
  z.literal('percent'),
  z.literal('currency'),
  z.literal('string'),
]);

/** API may send `null` when no tooltip is defined. */
const nullableTooltipSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => value ?? undefined);

const groupingsByKeySchema = z.record(
  z.string(),
  z.object({
    key: z.string(),
    label: z.string(),
    tooltip: nullableTooltipSchema,
  }),
);

const statColumnsByAliasSchema = z.record(
  z.string(),
  z.object({
    alias: z.string(),
    label: z.string(),
    tooltip: nullableTooltipSchema,
    tags: z.array(z.string()),
    type: statValueTypeSchema.optional(),
    format: z.string().optional(),
  }),
);

const displayColumnsByAliasSchema = z.record(
  z.string(),
  z.object({
    alias: z.string(),
    label: z.string(),
    tooltip: nullableTooltipSchema,
    tags: z.array(z.string()),
    entity: z.string().optional(),
    field: z.string().optional(),
    idColumnAlias: z.string(),
  }),
);

const statMetadataResponseSchema = z.object({
  statKey: z.string(),
  statName: z.string(),
  category: analyticsStatCategorySchema,
  factFamily: z.string(),
  timeGrain: z.union([z.literal('day'), z.literal('week'), z.literal('month')]),
  appliedLimitViewPublicId: z.string().nullable().optional(),
  customViewPublicId: z.string().optional(),
  allowed: z.object({
    groupBy: z.array(z.string()),
    statColumns: z.array(z.string()),
    displayColumns: z.array(z.string()),
  }),
  groupingsByKey: groupingsByKeySchema,
  statColumnsByAlias: statColumnsByAliasSchema,
  displayColumnsByAlias: displayColumnsByAliasSchema,
  /** Tag header order for Metrics section. When provided, headers are sorted by this array. */
  statColumnTagsSortOrder: z.array(z.string()).optional(),
  /** Tag header order for Details section. When provided, headers are sorted by this array. */
  displayColumnTagsSortOrder: z.array(z.string()).optional(),
});

export type AnalyticsStatMetadata = z.infer<typeof statMetadataResponseSchema>;

type ResponseData = AnalyticsStatMetadata | ErrorObjectString;

export type StatMetadataMode = 'default' | 'authoring';

export type StatMetadataQueryOptions = {
  mode?: StatMetadataMode;
  viewPublicId?: string;
  authoringStatSet?: Genie2ReportingMode | null;
};

function resolveMetadataMode(mode?: StatMetadataMode | AnalyticsMode): StatMetadataMode {
  if (mode === 'authoring') return 'authoring';
  if (mode === 'client') return 'default';
  return mode ?? 'default';
}

const getAnalyticsStatMetadata = async (
  accountId: number,
  accountType: 'client' | 'partner',
  statKey: string,
  options?: StatMetadataQueryOptions,
): Promise<AnalyticsStatMetadata> => {
  const params: Record<string, string> = {
    ...(options?.mode === 'authoring'
      ? analyticsQueryParams('authoring', options.authoringStatSet)
      : {}),
    ...(options?.viewPublicId ? { viewPublicId: options.viewPublicId } : {}),
  };
  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/${accountType}/${accountId}/analytics/stats/${statKey}/metadata`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      useNodeEndpoint: true,
      params: Object.keys(params).length > 0 ? params : undefined,
    },
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  return statMetadataResponseSchema.parse(data);
};

export const analyticsStatMetadataQueryOptions = (
  accountId: number,
  accountType: 'client' | 'partner',
  statKey: string,
  options?: {
    mode?: StatMetadataMode | AnalyticsMode;
    viewPublicId?: string;
    authoringStatSet?: Genie2ReportingMode | null;
  },
) => {
  const { mode, viewPublicId, authoringStatSet } = options ?? {};
  const metadataMode = resolveMetadataMode(mode);

  return queryOptions({
    queryKey: [
      'analytics-stats',
      'metadata',
      accountId,
      accountType,
      statKey,
      metadataMode,
      viewPublicId,
      analyticsAuthoringQueryKeyPart(
        metadataMode === 'authoring' ? 'authoring' : 'client',
        authoringStatSet,
      ),
    ],
    queryFn: () =>
      getAnalyticsStatMetadata(accountId, accountType, statKey, {
        mode: metadataMode,
        viewPublicId,
        authoringStatSet,
      }),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAnalyticsStatMetadataQuery = (
  statKey?: string,
  options?: { viewPublicId?: string },
) => {
  const { accountId, accountType, mode, authoringStatSet } = useAnalyticsStatsContextOrDefault();
  const metadataMode = statMetadataModeFromAnalyticsMode(mode);

  return useQuery({
    ...analyticsStatMetadataQueryOptions(accountId, accountType, statKey ?? '', {
      mode: metadataMode,
      viewPublicId: options?.viewPublicId,
      authoringStatSet: mode === 'authoring' ? authoringStatSet : undefined,
    }),
    enabled: !!accountId && !!statKey,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
};

/**
 * Staff-only: fetches metadata with mode=authoring to return all columns (not limited).
 * Use in ReportConfigurationEditor and other staff tools. Prefetch via route loader.
 */
export const staffStatMetadataQueryOptions = (
  accountId: number,
  accountType: 'client' | 'partner',
  statKey: string,
  authoringStatSet?: Genie2ReportingMode | null,
) =>
  analyticsStatMetadataQueryOptions(accountId, accountType, statKey, {
    mode: 'authoring',
    authoringStatSet,
  });

export const useStaffStatMetadataQuery = (
  accountId: string,
  accountType: 'client' | 'partner',
  statKey: string,
  authoringStatSet?: Genie2ReportingMode | null,
) =>
  useQuery({
    ...staffStatMetadataQueryOptions(Number(accountId), accountType, statKey, authoringStatSet),
    enabled: !!accountId && !!accountType && !!statKey,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

/**
 * Staff-only global metadata for report template authoring (no account context).
 * Same response shape as client report builder metadata.
 */
const getStaffAnalyticsStatMetadata = async (
  statKey: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
): Promise<AnalyticsStatMetadata> => {
  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/staff/analytics/stats/${statKey}/metadata`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(analyticsReportingMode),
    },
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  return statMetadataResponseSchema.parse(data);
};

export const staffAnalyticsStatMetadataQueryOptions = (
  statKey: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  queryOptions({
    queryKey: [
      'staff',
      'analytics-stats',
      'metadata',
      statKey,
      staffConfigModeQueryKeyPart(analyticsReportingMode),
    ],
    queryFn: () => getStaffAnalyticsStatMetadata(statKey, analyticsReportingMode),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStaffAnalyticsStatMetadataQuery = (
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  useQuery({
    ...staffAnalyticsStatMetadataQueryOptions(statKey ?? '', analyticsReportingMode),
    enabled: !!statKey,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
